<template>
  <div />
</template>

<script>

/**
 * Vue: Depict Collection (depict-collection)
 * -----------------------------------------------------------------------------
 * Component to make Depict Collection API calls.
 * https://api.depict.ai/docs#tag/Listing-(v3)/operation/Get_Listing_v3_listings__listing_id__get
 *
 * @param {String} merchant - Depict merchant required for the API.
 * @param {String} collection - Collection handle for categoryPath.
 * @param {Number} limit - Number of products to retrieve per page.
 * @param {Number} offset - Offset for pagination
 * @param {String} cursor - Cursor for pagination
 * @param {Array} selectedFilters - Currently selected filters.
 *
 */

export default {
  name: 'DepictCollection',

  props: {
    merchant: {
      type: String,
      default: '',
    },
    collection: {
      type: String,
      default: '',
    },
    limit: {
      type: Number,
      default: null,
    },
    offset: {
      type: Number,
      default: 0,
    },
    cursor: {
      type: String,
      default: null,
    },
    selectedFilters: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'collection-updated': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  watch: {

    /**
     * Watch selected filters update to get product results.
     */
    selectedFilters() {
      this.getCollectionProducts()
    },

    /**
     * Watch collection handle to get product results.
     */
    collection() {
      this.getCollectionProducts()
    },

    /**
     * Watch offset update to get product results.
     */
    offset() {
      if (!this.offset || isNaN(this.offset)) {
        return
      }

      this.getCollectionProducts('offset')
    },
  },

  mounted() {
    this.getCollectionProducts()
  },

  methods: {

    /**
     * Handle API call to Depict.
     * @param {String} action - Action carried out to call function.
     */
    async getCollectionProducts(action = 'default') {
      if (!this.collection) {
        return
      }

      try {
        const url = `https://api.depict.ai/v3/listings/external_id/${this.collection}/products`

        // Added top variables to stop line size stopping commits
        const petiteMarkets = window.cnvs.depict.petiteMarkets
        const marketHandle = window.cnvs.store.marketHandle
        const isPetiteRegion = petiteMarkets.includes(marketHandle)
        const petiteFilter = window.cnvs.depict.showPetite && isPetiteRegion ? ['true', 'false'] : ['false']

        const filters = [
          ...this.selectedFilters.map(filter => ({
            field: filter.key,
            data: filter.values,
            op: filter.op,
          })),
          {
            field: 'app_exclusive',
            data: false,
            op: 'eq',
          },
          {
            field: 'is_petite',
            op: 'in',
            data: petiteFilter,
          },

          /**
           * If no filters are selected, exclude products that are hidden
           * on the current collection.
           */
          ...this.selectedFilters.length === 0
            ? [
              {
                field: 'product_hide_on_collections',
                data: this.collection,
                op: 'nin',
              },
            ]
            : [],
        ]

        const queryObject = {
          merchant: this.merchant,
          market: cnvs.store.country.toLowerCase(),
          locale: cnvs.store.language,
          limit: this.limit,
          cursor: this.cursor,
          filters,
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(queryObject),
        })
        const data = await response.json()

        if (!isNaN(this.offset)) {
          data.offset = this.offset
        }

        this.$emit('collection-updated', { data, action })
      } catch (error) {
        throw new Error(error)
      }
    },
  },

}
</script>
